// frontendDerivationEngine.js - Simplified version using only backend IDs

// Helper to find a backend node by its ID
function findNodeById(taxonomyData, nodeId) {
    if (!nodeId) return null;
    
    // Recursive function to search through the tree
    function searchNode(node) {
        if (!node) return null;
        if (node.id === nodeId) return node;
        
        // Search in subcategories
        if (node.subcategories && Array.isArray(node.subcategories)) {
            for (const child of node.subcategories) {
                const found = searchNode(child);
                if (found) return found;
            }
        }
        return null;
    }
    
    // Search through all top-level nodes
    for (const key in taxonomyData) {
        const found = searchNode(taxonomyData[key]);
        if (found) return found;
    }
    
    return null;
}

// Collect attributes from a backend node and its children
function collectBackendAttributes(backendNode) {
    const attributes = new Set();
    
    // Add attributes from current node
    if (backendNode.attribute_ids && Array.isArray(backendNode.attribute_ids)) {
        backendNode.attribute_ids.forEach(id => attributes.add(id));
    }
    
    // Recursively collect from children
    if (backendNode.subcategories && Array.isArray(backendNode.subcategories)) {
        backendNode.subcategories.forEach(child => {
            const childAttributes = collectBackendAttributes(child);
            childAttributes.forEach(id => attributes.add(id));
        });
    }
    
    return attributes;
}

// Build the display path for a node (for UI purposes)
function getNodePath(taxonomyData, nodeId) {
    if (!nodeId) return '';
    
    function findPath(node, targetId, currentPath = []) {
        if (!node) return null;
        
        const newPath = [...currentPath, node.name];
        
        if (node.id === targetId) {
            return newPath;
        }
        
        if (node.subcategories && Array.isArray(node.subcategories)) {
            for (const child of node.subcategories) {
                const result = findPath(child, targetId, newPath);
                if (result) return result;
            }
        }
        
        return null;
    }
    
    // Search through all top-level nodes
    for (const key in taxonomyData) {
        const path = findPath(taxonomyData[key], nodeId, []);
        if (path) {
            return path.join(' > ');
        }
    }
    
    return nodeId; // Return the ID if path not found
}

// Aggregate attributes from various sources
function aggregateAttributes(node, backendTaxonomyData) {
    const definitionalAttributes = new Set(); // From conditions - determine membership
    const availableAttributes = new Set();    // From backend categories - for filtering/description
    
    // 1. DEFINITIONAL ATTRIBUTES: Add attributes from rule conditions
    if (node.derivation_rule && node.derivation_rule.conditions) {
        const conditions = node.derivation_rule.conditions;
        if (conditions.groups && Array.isArray(conditions.groups)) {
            conditions.groups.forEach(group => {
                if (group.conditions && Array.isArray(group.conditions)) {
                    group.conditions.forEach(condition => {
                        if (condition.attribute_id) {
                            definitionalAttributes.add(condition.attribute_id);
                        }
                    });
                }
            });
        }
    }
    
    // 2. AVAILABLE ATTRIBUTES: Add attributes from linked backend categories (using IDs)
    if (node.backend_category_ids && Array.isArray(node.backend_category_ids)) {
        node.backend_category_ids.forEach(categoryId => {
            const backendNode = findNodeById(backendTaxonomyData, categoryId);
            
            if (backendNode) {
                const backendAttributes = collectBackendAttributes(backendNode);
                backendAttributes.forEach(id => availableAttributes.add(id));
            } else {
                console.warn(`Backend node not found for ID: ${categoryId}`);
            }
        });
    }
    
    // 3. Recursively collect from children
    if (node.subcategories && Array.isArray(node.subcategories)) {
        node.subcategories.forEach(child => {
            const childAttributes = aggregateAttributes(child, backendTaxonomyData);
            // Merge both types of attributes from children
            childAttributes.definitional.forEach(id => definitionalAttributes.add(id));
            childAttributes.available.forEach(id => availableAttributes.add(id));
        });
    }
    
    return {
        definitional: Array.from(definitionalAttributes),
        available: Array.from(availableAttributes),
        // For backward compatibility, combine both for the main attributes list
        all: Array.from(new Set([...definitionalAttributes, ...availableAttributes]))
    };
}

// Enhance the tree with aggregated attributes
function enhanceTreeWithAttributes(generatedTree, backendTaxonomyData) {
    Object.keys(generatedTree).forEach(nodeId => {
        const node = generatedTree[nodeId];
        
        // Add aggregated attributes with clear distinction
        const attributes = aggregateAttributes(node, backendTaxonomyData);
        node.definitional_attributes = attributes.definitional; // For categorization logic
        node.available_attributes = attributes.available;       // For product filtering/display
        node.attribute_ids = attributes.all;                    // Combined for backward compatibility
        node.filters = attributes.all;                          // Keep filters in sync
        
        // Recursively enhance children
        if (node.subcategories && Array.isArray(node.subcategories)) {
            const childTree = {};
            node.subcategories.forEach(child => {
                if (child.id) {
                    childTree[child.id] = child;
                }
            });
            enhanceTreeWithAttributes(childTree, backendTaxonomyData);
        }
    });
}

// Processes a single rule definition to generate its corresponding node structure
function processSingleRule(rule, backendTaxonomyData) {
    if (!rule || !rule.name || !rule.id) {
        console.warn('[Engine] Skipping an invalid or incomplete rule:', rule);
        return null;
    }

    // Base structure for any generated node
    const baseNode = {
        id: rule.id,
        name: rule.name,
        subcategories: [],
        attribute_ids: [], // Will be populated by enhanceTreeWithAttributes
        filters: [],       // Will be populated by enhanceTreeWithAttributes
        isDerived: true,
        derivation_rule: rule, // Embed the rule for inspection
        type: rule.type || 'manual_node', // Default to manual_node if no type
        sources: {},
        // Store backend categories as IDs only
        backend_category_ids: rule.backend_category_ids || [],
        web_category_code: rule.web_category_code,           // Legacy single code
        web_category_codes: rule.web_category_codes || []    // Multiple codes array
    };

    let generatedNode = { ...baseNode };

    // Set appropriate styling based on node type and content
    if (rule.type === 'conditional') {
        generatedNode.className = 'node-derived-composite';
    } else if (rule.type === 'manual_node') {
        generatedNode.isDerived = false; // Manual nodes are not derived
        // Will be styled based on whether it has attributes after enhancement
    }
    
    // Process any explicit children of this rule (rules nested under rules)
    if (Array.isArray(rule.children)) {
        rule.children.forEach(childRule => {
            const childNode = processSingleRule(childRule, backendTaxonomyData);
            if (childNode) {
                generatedNode.subcategories.push(childNode);
            }
        });
    }

    return generatedNode;
}

/**
 * Main entry point for the derivation engine.
 * @param {object} frontendRules - The complete rules object.
 * @param {object} backendTaxonomyData - The source backend taxonomy.
 * @param {object} backendAttributeData - The source backend attributes.
 * @returns {object} The generated frontend tree object.
 */
function generateFrontendTree(frontendRules, backendTaxonomyData, backendAttributeData) {
    if (!frontendRules || !Array.isArray(frontendRules.categories)) {
        return {};
    }

    const rules = frontendRules.categories;
    
    // Process all top-level rules
    const generatedTree = {};
    rules.forEach(rule => {
        const node = processSingleRule(rule, backendTaxonomyData);
        if (node) {
            generatedTree[node.id] = node;
        }
    });

    // Enhance tree with aggregated attributes (both definitional and available)
    enhanceTreeWithAttributes(generatedTree, backendTaxonomyData);
    
    // Apply styling based on final attribute state
    Object.values(generatedTree).forEach(node => {
        applyFinalStyling(node);
    });
    
    return generatedTree;
}

// Apply final styling based on attribute state
function applyFinalStyling(node) {
    if (node.type === 'conditional') {
        node.className = 'node-derived-composite';
    } else if (node.type === 'manual_node') {
        node.isDerived = false;
        node.className = (node.attribute_ids && node.attribute_ids.length > 0) ? 'node-with-attributes' : '';
    } else {
        node.className = (node.attribute_ids && node.attribute_ids.length > 0) ? 'node-with-attributes' : '';
    }
    
    // Recursively apply to children
    if (node.subcategories && Array.isArray(node.subcategories)) {
        node.subcategories.forEach(child => applyFinalStyling(child));
    }
}

// Get all backend category IDs from taxonomy
function getBackendCategoryIds(taxonomyData) {
    const ids = [];
    function traverse(node) {
        if (!node) return;
        if (node.id) {
            ids.push(node.id);
        }
        if (Array.isArray(node.subcategories)) {
            node.subcategories.forEach(sub => traverse(sub));
        }
    }
    Object.values(taxonomyData).forEach(topLevelNode => traverse(topLevelNode));
    return ids;
}

// Build category display information (ID to path and name)
function getCategoryDisplayInfo(taxonomyData) {
    const displayInfo = {};
    
    function traverse(node, currentPath = []) {
        if (!node || !node.name) return;
        
        const newPath = [...currentPath, node.name];
        
        if (node.id) {
            displayInfo[node.id] = {
                name: node.name,
                path: newPath.join(' > ')
            };
        }
        
        if (Array.isArray(node.subcategories)) {
            node.subcategories.forEach(sub => traverse(sub, newPath));
        }
    }
    
    Object.values(taxonomyData).forEach(topLevelNode => traverse(topLevelNode, []));
    return displayInfo;
}

// Get all backend category paths (kept for compatibility/display purposes)
function getBackendCategoryPaths(taxonomyData) {
    const paths = new Set();
    function traverse(node, currentPathSegments) {
        if (!node || !node.name) return;
        const newPath = [...currentPathSegments, node.name];
        paths.add(newPath.join('|'));
        if (Array.isArray(node.subcategories)) {
            node.subcategories.forEach(sub => traverse(sub, newPath));
        }
    }
    Object.values(taxonomyData).forEach(topLevelNode => traverse(topLevelNode, []));
    return Array.from(paths).sort();
}

export { 
    generateFrontendTree, 
    getBackendCategoryIds,
    getCategoryDisplayInfo,
    getNodePath,
    findNodeById,
    getBackendCategoryPaths
};
